import { useEffect, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';

function MapController({ center, zoom, bounds, onMapReady }) {
  const map = useMap();
  const [isReady, setIsReady] = useState(false);

  // Wait for map to be fully initialized
  useEffect(() => {
    if (!map) return;

    const checkReady = () => {
      if (map._loaded && map._container) {
        setIsReady(true);
        if (onMapReady) {
          onMapReady(map);
        }
      }
    };

    // Check immediately
    checkReady();

    // Also listen for load event if not ready yet
    if (!map._loaded) {
      map.on('load', checkReady);
    }

    return () => {
      map.off('load', checkReady);
    };
  }, [map, onMapReady]);

  // Update view only when map is ready
  useEffect(() => {
    if (!isReady || !map || !center) return;

    if (
      typeof center.lat === "number" &&
      typeof center.lng === "number" &&
      !isNaN(center.lat) &&
      !isNaN(center.lng)
    ) {
      try {
        // Use setTimeout to ensure DOM is ready
        setTimeout(() => {
          if (map && map._container) {
            map.setView([center.lat, center.lng], zoom, {
              animate: false // Disable animation to avoid conflicts
            });
          }
        }, 0);
      } catch (e) {
        console.error("Error setting map view:", e);
      }
    }
  }, [center, zoom, map, isReady]);

  // Handle bounds changes
  useEffect(() => {
    if (!isReady || !map || !bounds) return;

    try {
      const minlat = parseFloat(bounds.minlat);
      const minlon = parseFloat(bounds.minlon);
      const maxlat = parseFloat(bounds.maxlat);
      const maxlon = parseFloat(bounds.maxlon);

      if (
        !Number.isFinite(minlat) ||
        !Number.isFinite(minlon) ||
        !Number.isFinite(maxlat) ||
        !Number.isFinite(maxlon)
      ) {
        return;
      }

      const leafletBounds = L.latLngBounds([minlat, minlon], [maxlat, maxlon]);
      
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (map && map._container) {
          try {
            map.setMaxBounds(leafletBounds);
            
            const currentCenter = map.getCenter();
            if (!leafletBounds.contains(currentCenter)) {
              map.fitBounds(leafletBounds, { 
                padding: [50, 50],
                animate: false
              });
            }
          } catch (err) {
            console.error("Error applying bounds:", err);
          }
        }
      }, 100);
    } catch (err) {
      console.error("Error processing bounds:", err);
    }
  }, [bounds, map, isReady]);

  return null;
}

export default MapController;